import React, {useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Card,
    Chip,
    Collapse,
    CircularProgress,
    Divider,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {Cancel as CancelIcon, ExpandLess, ExpandMore, PlayArrow, Stop, Undo} from "@mui/icons-material";
import {Delete as DeleteIcon, PictureAsPdf} from "@mui/icons-material";
import moment from "moment";
import API from "../../../../utils/API";
import {API_ENDPOINTS} from "../../../../apiConfig";
import Forms from "../../../../components/Forms";
import Error from "../../../../components/Error";
import {useAuth} from "../../../../contexts/AuthContext";
import {useModal} from "../../../../contexts/ModalContext";
import {CANCEL_REPAIR_ALLOWED_STATES} from "../../../../utils/config";
import {formatPrice} from "../../../../utils/tools";

const TERMINAL_STATUSES = new Set(["completed", "customerRefused", "technicianRefused", "cancelled", "not_repairable"]);
const STATUS_OPTIONS = [
    "pending",
    "in_progress",
    "paused",
    "waiting_parts",
    "waiting_customer",
    "waiting_external",
    "blocked",
    "completed",
    "customerRefused",
    "technicianRefused",
    "cancelled",
    "not_repairable"
];

const RESOLUTION_OPTIONS = [
    "fixed",
    "customer_declined",
    "device_irreparable",
    "no_parts_available",
    "uneconomical",
    "wrong_diagnosis",
    "duplicate_task",
    "cancelled_by_shop",
    "cancelled_by_customer"
];

const STATUS_COLORS = {
    pending: {bg: "#fff7e6", border: "#f7c873", chip: "warning"},
    in_progress: {bg: "#eaf5ff", border: "#64a8ff", chip: "info"},
    paused: {bg: "#f3f4f6", border: "#9ca3af", chip: "default"},
    waiting_parts: {bg: "#eefcf3", border: "#4ade80", chip: "success"},
    waiting_customer: {bg: "#eff6ff", border: "#60a5fa", chip: "info"},
    waiting_external: {bg: "#f5f3ff", border: "#a78bfa", chip: "secondary"},
    blocked: {bg: "#fff1f2", border: "#fb7185", chip: "error"},
    completed: {bg: "#ecfdf3", border: "#34d399", chip: "success"},
    customerRefused: {bg: "#fff7ed", border: "#fb923c", chip: "warning"},
    technicianRefused: {bg: "#fef2f2", border: "#f87171", chip: "error"},
    cancelled: {bg: "#f3f4f6", border: "#9ca3af", chip: "default"},
    not_repairable: {bg: "#fef2f2", border: "#ef4444", chip: "error"},
};

const taskKey = (deviceRepairId, repairListId) => `${deviceRepairId}-${repairListId}`;
const DISCOUNT_TYPE_OPTIONS = [
    {value: "fixed", label: "Fixní"},
    {value: "percent", label: "Procentuální"}
];
const parseHHMMToMinutes = (value) => {
    if (!value || !value.includes(":")) return 0;
    const [hh, mm] = value.split(":").map((x) => Number(x));
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return 0;
    if (hh < 0 || mm < 0 || mm > 59) return 0;
    return hh * 60 + mm;
};
const getPartUnitCost = (part) => {
    if (!part || typeof part !== "object") return 0;
    const values = [
        Number(part?.averagePurchasePricePerUnit?.gross),
        Number(part?.averagePurchasePricePerUnit?.net),
        Number(part?.expectedPurchasePricePerUnit?.gross),
        Number(part?.expectedPurchasePricePerUnit?.net)
    ];
    const found = values.find((v) => Number.isFinite(v) && v >= 0);
    return Number.isFinite(found) ? found : 0;
};
const getTaskPrice = (task) => {
    const value = Number(task?.price);
    return Number.isFinite(value) && value > 0 ? value : 0;
};
const getTaskPartsCost = (task, partByIdMap) => {
    return (task?.partsUsed || []).reduce((sum, usage) => {
        const part = usage?.part?._id ? usage.part : partByIdMap[usage?.part];
        return sum + getPartUnitCost(part);
    }, 0);
};
const getDiscountAmount = (task, discountType, discountValue) => {
    const price = getTaskPrice(task);
    const value = Number(discountValue);
    if (!Number.isFinite(value) || value < 0) return 0;
    if (discountType === "percent") {
        return Math.min(price, Math.max(0, (price * value) / 100));
    }
    return Math.min(price, Math.max(0, value));
};

const EditRepair = () => {
    const {id} = useParams();
    const {user} = useAuth();
    const navigate = useNavigate();
    const {t} = useTranslation();
    const {confirm} = useModal();

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState();
    const [repair, setRepair] = useState(null);
    const [partList, setPartList] = useState([]);
    const [deviceCatalogMap, setDeviceCatalogMap] = useState({});
    const [devicePartsMap, setDevicePartsMap] = useState({});
    const [userOptions, setUserOptions] = useState([]);
    const [selectedTechnicians, setSelectedTechnicians] = useState({});
    const [statusDrafts, setStatusDrafts] = useState({});
    const [manualLogs, setManualLogs] = useState({});
    const [manualLogsOpen, setManualLogsOpen] = useState({});
    const [workLockInfo, setWorkLockInfo] = useState(null);
    const [notifyResult, setNotifyResult] = useState(null);
    const [discountDrafts, setDiscountDrafts] = useState({});
    const [discountWarnings, setDiscountWarnings] = useState({});
    const [addRepairItemDrafts, setAddRepairItemDrafts] = useState({});

    const loadAll = async () => {
        setIsLoading(true);
        setError(undefined);
        try {
            const [repairRes, partRes, userRes, deviceRes] = await Promise.all([
                API.get(API_ENDPOINTS.repairs.get(id)),
                API.get(API_ENDPOINTS.partSku.listByRepair(id)),
                API.get(API_ENDPOINTS.user.list),
                API.get(API_ENDPOINTS.device.list)
            ]);
            setRepair(repairRes.data);
            setPartList(partRes.data || []);
            setUserOptions((userRes.data || []).map((u) => ({
                value: u._id,
                label: `${u.name} ${u.surname}`.trim()
            })));
            const catalogMap = (deviceRes?.data || []).reduce((acc, device) => {
                acc[device._id] = device;
                return acc;
            }, {});
            setDeviceCatalogMap(catalogMap);
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadAll();
    }, [id]);

    useEffect(() => {
        setDevicePartsMap((partList || []).reduce((acc, part) => {
            (part?.part?.compatibleWith || []).forEach((deviceRef) => {
                const deviceId = String(deviceRef?._id || deviceRef);
                if (!acc[deviceId]) {
                    acc[deviceId] = [];
                }
                acc[deviceId].push(part);
            });
            return acc;
        }, {}));
    }, [partList]);

    const estimatedFromItems = useMemo(() => {
        if (!repair) return 0;
        return (repair.repairs || []).reduce((sum, dev) => {
            return sum + (dev.repairList || []).reduce((subSum, task) => subSum + (Number(task.price) || 0), 0);
        }, 0);
    }, [repair]);
    const partByIdMap = useMemo(() => {
        return (partList || []).reduce((acc, part) => {
            acc[part._id] = part;
            return acc;
        }, {});
    }, [partList]);

    const getStatusLabel = (status) => t(`repairStatus.${status}`) || status;

    const getStatusColors = (status) => STATUS_COLORS[status] || STATUS_COLORS.pending;

    const getTechnician = (taskId) => selectedTechnicians[taskId] || user?._id;

    const updateTechnician = (taskId, technicianId) => {
        setSelectedTechnicians((prev) => ({...prev, [taskId]: technicianId}));
    };

    const updateStatusDraft = (taskId, patch) => {
        setStatusDrafts((prev) => ({...prev, [taskId]: {...prev[taskId], ...patch}}));
    };

    const updateManualLogDraft = (taskId, patch) => {
        setManualLogs((prev) => ({...prev, [taskId]: {...prev[taskId], ...patch}}));
    };
    const toggleManualLog = (taskId) => {
        setManualLogsOpen((prev) => ({...prev, [taskId]: !prev[taskId]}));
    };
    const updateDiscountDraft = (taskId, patch) => {
        setDiscountDrafts((prev) => ({...prev, [taskId]: {...prev[taskId], ...patch}}));
    };
    const updateAddRepairItemDraft = (deviceRepairId, value) => {
        setAddRepairItemDrafts((prev) => ({...prev, [deviceRepairId]: value}));
    };

    const reloadRepair = async () => {
        try {
            const {data} = await API.get(API_ENDPOINTS.repairs.get(id));
            setRepair(data);
        } catch (e) {
            setError(e);
        }
    };

    const startWork = async (deviceRepairId, repairListId) => {
        try {
            setWorkLockInfo(null);
            await API.put(API_ENDPOINTS.repairs.startWork(repair._id), {
                deviceRepairId,
                repairListId,
                technicianId: getTechnician(taskKey(deviceRepairId, repairListId))
            });
            await reloadRepair();
        } catch (e) {
            if (e?.response?.status === 409) {
                setWorkLockInfo("Na tomto úkonu už aktuálně pracuje jiný technik.");
            }
            setError(e);
        }
    };

    const stopWork = async (deviceRepairId, repairListId) => {
        try {
            const key = taskKey(deviceRepairId, repairListId);
            await API.put(API_ENDPOINTS.repairs.stopWork(repair._id), {
                deviceRepairId,
                repairListId,
                technicianId: getTechnician(key)
            });
            await reloadRepair();
        } catch (e) {
            setError(e);
        }
    };

    const logWork = async (deviceRepairId, repairListId) => {
        const key = taskKey(deviceRepairId, repairListId);
        const draft = manualLogs[key] || {};
        const durationMinutes = parseHHMMToMinutes(draft.durationHHMM);
        if (!durationMinutes) return;

        try {
            await API.put(API_ENDPOINTS.repairs.logWork(repair._id), {
                deviceRepairId,
                repairListId,
                technicianId: getTechnician(key),
                durationMinutes,
                note: draft.note
            });
            setManualLogs((prev) => ({...prev, [key]: {}}));
            await reloadRepair();
        } catch (e) {
            setError(e);
        }
    };

    const updateTaskStatus = async (deviceRepairId, repairListId) => {
        const key = taskKey(deviceRepairId, repairListId);
        const draft = statusDrafts[key] || {};
        if (!draft.status) return;
        try {
            await API.put(API_ENDPOINTS.repairs.taskStatus(repair._id), {
                deviceRepairId,
                repairListId,
                status: draft.status,
                resolution: draft.resolution || undefined,
                note: draft.note || undefined
            });
            await reloadRepair();
        } catch (e) {
            setError(e);
        }
    };

    const addRepairNote = async (deviceRepairId, repairListId, noteText) => {
        const note = noteText.trim();
        if (!note) return "";
        try {
            await API.put(API_ENDPOINTS.repairs.addNote(repair._id), {deviceRepairId, repairListId, note});
            await reloadRepair();
            return "";
        } catch (e) {
            setError(e);
            return noteText;
        }
    };

    const addPartToItem = async (deviceRepairId, repairListId, partId) => {
        try {
            const {data} = await API.put(API_ENDPOINTS.repairs.addPart(repair._id), {
                deviceRepairId,
                repairListId,
                part: partId
            });
            setRepair(data.repair);
            setPartList((prevState) => prevState.map((p) => p._id === data.part._id ? data.part : p));
        } catch (e) {
            setError(e);
        }
    };

    const removePartFromItem = async (deviceRepairId, repairListId, partUsageId) => {
        try {
            const {data} = await API.put(API_ENDPOINTS.repairs.removePart(repair._id), {
                deviceRepairId,
                repairListId,
                partUsageId
            });
            setRepair(data.repair);
            setPartList((prevState) => prevState.map((p) => p._id === data.part._id ? data.part : p));
        } catch (e) {
            setError(e);
        }
    };
    const saveTaskDiscount = async (deviceRepairId, repairListId, task) => {
        const key = taskKey(deviceRepairId, repairListId);
        const draft = discountDrafts[key] || {};
        const discountType = draft.discountType || task.discount?.type || "fixed";
        const discountValue = draft.discountValue ?? task.discount?.value ?? 0;
        try {
            const {data} = await API.put(API_ENDPOINTS.repairs.taskDiscount(repair._id), {
                deviceRepairId,
                repairListId,
                discountType,
                discountValue: Number(discountValue) || 0
            });
            setRepair(data.repair);
            setDiscountWarnings((prev) => ({...prev, [key]: data.warning || null}));
        } catch (e) {
            setError(e);
        }
    };
    const addRepairItemToDevice = async (deviceRepairId) => {
        const selectedCatalogItemId = addRepairItemDrafts[deviceRepairId];
        if (!selectedCatalogItemId) return;
        try {
            await API.put(API_ENDPOINTS.repairs.addRepairItem(repair._id), {
                deviceRepairId,
                deviceRepairCatalogId: selectedCatalogItemId
            });
            setAddRepairItemDrafts((prev) => ({...prev, [deviceRepairId]: ""}));
            await reloadRepair();
        } catch (e) {
            setError(e);
        }
    };

    const downloadIntakeProtocolPdf = async () => {
        try {
            const response = await API.get(API_ENDPOINTS.repairs.intakeProtocolPdf(repair._id), {
                responseType: "blob"
            });
            const blob = new Blob([response.data], {type: "application/pdf"});
            const href = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = href;
            a.download = `repair-intake-${repair.repairNumber || repair._id}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(href);
        } catch (e) {
            setError(e);
        }
    };

    const downloadInvoicePdf = async () => {
        if (repair.status !== "completed") return;
        try {
            const response = await API.get(API_ENDPOINTS.repairs.invoicePdf(repair._id), {
                responseType: "blob"
            });
            const blob = new Blob([response.data], {type: "application/pdf"});
            const href = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = href;
            a.download = `repair-invoice-${repair.repairNumber || repair._id}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(href);
        } catch (e) {
            setError(e);
        }
    };

    const notifyCustomerCompletion = async () => {
        try {
            const {data} = await API.put(API_ENDPOINTS.repairs.notifyCompletion(repair._id), {
                channels: ["email", "sms"]
            });
            setRepair(data.repair);
            setNotifyResult(data.notificationResult);
        } catch (e) {
            setError(e);
        }
    };

    const cancelWholeRepair = async () => {
        const result = await confirm(`Opravdu chcete zrušit opravu ${repair.repairNumber || repair._id}?`);
        if (!result) return;
        try {
            const {data} = await API.put(API_ENDPOINTS.repairs.cancel(repair._id));
            setRepair(data);
        } catch (e) {
            setError(e);
        }
    };

    const renderOwnerName = () => {
        if (repair?.customer) {
            return repair.customer.type === "person"
                ? `${repair.customer?.person?.name || ""} ${repair.customer?.person?.surname || ""}`.trim()
                : repair.customer?.company?.name;
        }
        if (repair?.buyback) {
            const buybackLabel = repair?.buyback?.buybackNumber || repair?.buyback?._id || String(repair.buyback);
            return `Výkup ${buybackLabel || ""}`.trim();
        }
        return "Interní zařízení";
    };

    const renderOwnerSubLabel = () => {
        if (repair?.customer || repair?.buyback) return "Externí zákazník";
        return "Interní";
    };

    const renderOwnerEmail = () => {
        if (repair?.customer) {
            return repair.customer?.emails?.[0]?.value || "-";
        }
        return "-";
    };

    const renderOwnerPhone = () => {
        if (repair?.customer) {
            return repair.customer?.phones?.[0]?.value || "-";
        }
        return "-";
    };

    if (isLoading) {
        return (
            <Box className="flex items-center justify-center h-80">
                <CircularProgress/>
            </Box>
        );
    }
    if (error) {
        return (
            <Box className="p-6">
                <Error error={error}/>
                <Forms.Button className="mt-4" label={t("reload")} onClick={loadAll}/>
            </Box>
        );
    }
    if (!repair) return null;

    const repairClosed = repair.status === "completed" || repair.status === "cancelled";
    const isBuybackRepair = Boolean(repair?.buyback);
    const canCancelWhole = CANCEL_REPAIR_ALLOWED_STATES.includes(repair.status);
    const repairRef = repair.repairNumber || repair._id;

    return (
        <Box className="p-6 space-y-6">
            <Box className="flex items-start justify-between gap-4">
                <div>
                    <Typography variant="h5">{t("repairs.detail")}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        #{repairRef} | {t("createdAt")}: {new Date(repair.createdAt).toLocaleString("cs-CZ")}
                    </Typography>
                </div>
                <Stack direction="row" spacing={1}>
                    {!isBuybackRepair && (
                        <Button
                            variant="contained"
                            color="success"
                            disabled={repair.status !== "completed"}
                            onClick={notifyCustomerCompletion}
                        >
                            Informovat zákazníka
                        </Button>
                    )}
                    {!isBuybackRepair && (
                        <Button
                            variant="outlined"
                            startIcon={<PictureAsPdf/>}
                            onClick={downloadIntakeProtocolPdf}
                        >
                            Přebírací protokol PDF
                        </Button>
                    )}
                    {!isBuybackRepair && (
                        <Button
                            variant="outlined"
                            startIcon={<PictureAsPdf/>}
                            disabled={repair.status !== "completed"}
                            onClick={downloadInvoicePdf}
                        >
                            Faktura PDF
                        </Button>
                    )}
                    {canCancelWhole && (
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<CancelIcon/>}
                            onClick={cancelWholeRepair}
                            disabled={repairClosed}
                        >
                            {t("repairs.cancelRepair")}
                        </Button>
                    )}
                    <Forms.Button variant="text" leftIcon={<Undo/>} label={t("back")} onClick={() => navigate(-1)}/>
                </Stack>
            </Box>

            <Card className="p-4">
                <Stack direction={{xs: "column", md: "row"}} spacing={4} justifyContent="space-between">
                    <Box>
                        <Typography variant="h6">{renderOwnerName()}</Typography>
                        <Typography variant="body2" color="text.secondary">{renderOwnerSubLabel()}</Typography>
                        <Typography variant="body2" color="text.secondary">{renderOwnerEmail()}</Typography>
                        <Typography variant="body2" color="text.secondary">{renderOwnerPhone()}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">{t("status")}</Typography>
                        <Typography variant="h6">{getStatusLabel(repair.status)}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t("repairs.dueDate")}: {repair.dueDate ? moment(repair.dueDate).format("DD.MM.YYYY HH:mm") : "-"}
                        </Typography>
                        <Typography variant="h6" className="mt-2">{formatPrice(estimatedFromItems)}</Typography>
                    </Box>
                </Stack>
            </Card>

            {(repair.repairs || []).map((deviceRepair) => {
                const deviceName = typeof deviceRepair.device === "object" ? deviceRepair.device?.name : String(deviceRepair.device);
                const deviceId = typeof deviceRepair.device === "object" ? deviceRepair.device?._id : deviceRepair.device;

                return (
                    <Card key={deviceRepair._id} className="p-4">
                        <Stack direction={{xs: "column", md: "row"}} spacing={2} justifyContent="space-between" className="mb-3">
                            <Typography variant="h6">{deviceName}</Typography>
                            <Stack direction={{xs: "column", md: "row"}} spacing={1.5} alignItems={{xs: "stretch", md: "center"}}>
                                <TextField
                                    select
                                    size="small"
                                    label="Přidat úkon"
                                    value={addRepairItemDrafts[deviceRepair._id] || ""}
                                    onChange={(e) => updateAddRepairItemDraft(deviceRepair._id, e.target.value)}
                                    sx={{minWidth: 320}}
                                    disabled={repairClosed}
                                >
                                    <MenuItem value="">Vyberte úkon</MenuItem>
                                    {((deviceCatalogMap[deviceId]?.repairList || [])
                                        .filter((catalogItem) => catalogItem?.active !== false)
                                        .filter((catalogItem) => !(deviceRepair.repairList || []).some((task) =>
                                            String(task.deviceRepair?._id || task.deviceRepair) === String(catalogItem._id)
                                        ))
                                        .map((catalogItem) => (
                                            <MenuItem key={catalogItem._id} value={catalogItem._id}>
                                                {catalogItem?.repair?.name || "Úkon"} ({formatPrice(catalogItem?.price || 0)})
                                            </MenuItem>
                                        )))}
                                </TextField>
                                <Button
                                    variant="outlined"
                                    disabled={repairClosed || !addRepairItemDrafts[deviceRepair._id]}
                                    onClick={() => addRepairItemToDevice(deviceRepair._id)}
                                >
                                    Přidat
                                </Button>
                            </Stack>
                        </Stack>
                        <Stack spacing={2}>
                            {(deviceRepair.repairList || []).map((task) => {
                                const key = taskKey(deviceRepair._id, task._id);
                                const colors = getStatusColors(task.status);
                                const draftStatus = statusDrafts[key]?.status || task.status;
                                const openSession = (task.workSessions || []).find((session) => !session.endedAt);
                                const activeSession = Boolean(openSession);
                                const selectedTechnician = getTechnician(key)?.toString();
                                const openTechnician = openSession?.technician?._id
                                    ? openSession.technician._id.toString()
                                    : openSession?.technician?.toString();
                                const lockByAnotherTechnician = activeSession && openTechnician && selectedTechnician && openTechnician !== selectedTechnician;
                                const openTechnicianName = openSession?.technician?.name
                                    ? `${openSession.technician.name} ${openSession.technician.surname || ""}`.trim()
                                    : "";
                                const currentDiscountType = discountDrafts[key]?.discountType || task.discount?.type || "fixed";
                                const currentDiscountValue = discountDrafts[key]?.discountValue ?? task.discount?.value ?? 0;
                                const taskPrice = getTaskPrice(task);
                                const draftDiscountAmount = getDiscountAmount(task, currentDiscountType, currentDiscountValue);
                                const discountedTaskPrice = Math.max(0, taskPrice - draftDiscountAmount);
                                const taskPartsCost = getTaskPartsCost(task, partByIdMap);
                                const showPartsCostWarning = discountedTaskPrice < taskPartsCost;
                                const canEditDiscount = TERMINAL_STATUSES.has(task.status);

                                return (
                                    <Card
                                        key={task._id}
                                        className="p-4"
                                        sx={{backgroundColor: colors.bg, border: `2px solid ${colors.border}`}}
                                    >
                                        <Stack spacing={2}>
                                            <Stack direction={{xs: "column", md: "row"}} spacing={2} justifyContent="space-between">
                                                <Box>
                                                    <Typography variant="subtitle1" className="font-semibold">
                                                        {task.repairCatalogRepair?.name || task.repairCatalogRepair}
                                                    </Typography>
                                                    <Stack direction="row" spacing={1} alignItems="center" className="mt-1">
                                                        <Chip
                                                            size="small"
                                                            color={colors.chip}
                                                            label={getStatusLabel(task.status)}
                                                            variant="filled"
                                                        />
                                                        {activeSession && <Chip size="small" color="success" label="Aktivní práce"/>}
                                                        {activeSession && openTechnicianName && (
                                                            <Chip size="small" color="primary" variant="outlined" label={`Pracuje: ${openTechnicianName}`}/>
                                                        )}
                                                        <Chip size="small" variant="outlined" label={`${t("repairs.workedMinutes")}: ${task.workedMinutes || 0} min`}/>
                                                    </Stack>
                                                </Box>
                                                <Stack direction={{xs: "column", md: "row"}} spacing={1.5} alignItems={{xs: "stretch", md: "center"}}>
                                                    <TextField
                                                        select
                                                        size="small"
                                                        label="Technik"
                                                        value={getTechnician(key)}
                                                        onChange={(e) => updateTechnician(key, e.target.value)}
                                                        sx={{minWidth: 180}}
                                                    >
                                                        {userOptions.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                        ))}
                                                    </TextField>
                                                    <ButtonGroup variant="outlined" size="small">
                                                        <Button
                                                            startIcon={<PlayArrow/>}
                                                            disabled={repairClosed || TERMINAL_STATUSES.has(task.status) || lockByAnotherTechnician}
                                                            onClick={() => startWork(deviceRepair._id, task._id)}
                                                        >
                                                            {t("repairs.startWork")}
                                                        </Button>
                                                        <Button
                                                            startIcon={<Stop/>}
                                                            disabled={repairClosed || !activeSession}
                                                            onClick={() => stopWork(deviceRepair._id, task._id)}
                                                        >
                                                            {t("repairs.stopWork")}
                                                        </Button>
                                                    </ButtonGroup>
                                                    <Tooltip title={t("repairs.manualWorkLog")}>
                                                        <IconButton size="small" onClick={() => toggleManualLog(key)}>
                                                            {manualLogsOpen[key] ? <ExpandLess/> : <ExpandMore/>}
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Box className="min-w-24 text-right">
                                                        <Typography variant="body2" color="text.secondary">{t("price")}</Typography>
                                                        <Typography variant="h6">{formatPrice(task.price || 0)}</Typography>
                                                    </Box>
                                                </Stack>
                                            </Stack>

                                            <Collapse in={Boolean(manualLogsOpen[key])}>
                                                <Divider className="mb-2"/>
                                                <Stack direction={{xs: "column", lg: "row"}} spacing={2} alignItems="center">
                                                    <TextField
                                                        size="small"
                                                        type="time"
                                                        label="Čas (hh:mm)"
                                                        InputLabelProps={{shrink: true}}
                                                        inputProps={{step: 60}}
                                                        value={manualLogs[key]?.durationHHMM || ""}
                                                        onChange={(e) => updateManualLogDraft(key, {durationHHMM: e.target.value})}
                                                        sx={{maxWidth: 150}}
                                                        disabled={repairClosed}
                                                    />
                                                    <TextField
                                                        size="small"
                                                        label={t("note")}
                                                        value={manualLogs[key]?.note || ""}
                                                        onChange={(e) => updateManualLogDraft(key, {note: e.target.value})}
                                                        sx={{minWidth: 260}}
                                                        disabled={repairClosed}
                                                    />
                                                    <Button
                                                        variant="outlined"
                                                        disabled={repairClosed}
                                                        onClick={() => logWork(deviceRepair._id, task._id)}
                                                    >
                                                        {t("repairs.logWork")}
                                                    </Button>
                                                </Stack>
                                            </Collapse>

                                            <Stack direction={{xs: "column", lg: "row"}} spacing={2} alignItems="center">
                                                <TextField
                                                    select
                                                    size="small"
                                                    label={t("repairs.setStatus")}
                                                    value={draftStatus}
                                                    onChange={(e) => updateStatusDraft(key, {status: e.target.value})}
                                                    sx={{minWidth: 220}}
                                                    disabled={repairClosed}
                                                >
                                                    {STATUS_OPTIONS.map((status) => (
                                                        <MenuItem key={status} value={status}>{getStatusLabel(status)}</MenuItem>
                                                    ))}
                                                </TextField>
                                                <TextField
                                                    select
                                                    size="small"
                                                    label={t("repairs.resolution")}
                                                    value={statusDrafts[key]?.resolution || ""}
                                                    onChange={(e) => updateStatusDraft(key, {resolution: e.target.value})}
                                                    sx={{minWidth: 240}}
                                                    disabled={repairClosed || !TERMINAL_STATUSES.has(draftStatus)}
                                                >
                                                    <MenuItem value="">-</MenuItem>
                                                    {RESOLUTION_OPTIONS.map((option) => (
                                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                                    ))}
                                                </TextField>
                                                <TextField
                                                    size="small"
                                                    label={t("note")}
                                                    value={statusDrafts[key]?.note || ""}
                                                    onChange={(e) => updateStatusDraft(key, {note: e.target.value})}
                                                    sx={{minWidth: 260}}
                                                    disabled={repairClosed}
                                                />
                                                <Button
                                                    variant="contained"
                                                    disabled={repairClosed}
                                                    onClick={() => updateTaskStatus(deviceRepair._id, task._id)}
                                                >
                                                    {t("save")}
                                                </Button>
                                            </Stack>

                                            <Divider/>

                                            {!isBuybackRepair && (
                                                <>
                                                    <Stack spacing={1.5}>
                                                        <Typography variant="subtitle2">Sleva na opravu</Typography>
                                                        <Stack direction={{xs: "column", lg: "row"}} spacing={2} alignItems="center">
                                                            <TextField
                                                                select
                                                                size="small"
                                                                label="Typ slevy"
                                                                value={currentDiscountType}
                                                                onChange={(e) => updateDiscountDraft(key, {discountType: e.target.value})}
                                                                sx={{minWidth: 170}}
                                                                disabled={!canEditDiscount}
                                                            >
                                                                {DISCOUNT_TYPE_OPTIONS.map((option) => (
                                                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                                ))}
                                                            </TextField>
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                label={currentDiscountType === "percent" ? "Sleva (%)" : "Sleva (Kč)"}
                                                                value={currentDiscountValue}
                                                                onChange={(e) => updateDiscountDraft(key, {discountValue: e.target.value})}
                                                                inputProps={{
                                                                    min: 0,
                                                                    max: currentDiscountType === "percent" ? 100 : taskPrice,
                                                                    step: currentDiscountType === "percent" ? 1 : 1
                                                                }}
                                                                sx={{minWidth: 170}}
                                                                disabled={!canEditDiscount}
                                                            />
                                                            <Button
                                                                variant="contained"
                                                                onClick={() => saveTaskDiscount(deviceRepair._id, task._id, task)}
                                                                disabled={!canEditDiscount}
                                                            >
                                                                Uložit slevu
                                                            </Button>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Po slevě: {formatPrice(discountedTaskPrice)}
                                                            </Typography>
                                                        </Stack>
                                                        {!canEditDiscount && (
                                                            <Alert severity="info">Slevu lze nastavit až po ukončení dané opravy.</Alert>
                                                        )}
                                                        {showPartsCostWarning && (
                                                            <Alert severity="warning">
                                                                Sleva nepokrývá náklady opravy. Cena dílů ({formatPrice(taskPartsCost)}) je vyšší než výsledná cena opravy ({formatPrice(discountedTaskPrice)}).
                                                            </Alert>
                                                        )}
                                                        {discountWarnings[key] && (
                                                            <Alert severity="warning">{discountWarnings[key]}</Alert>
                                                        )}
                                                    </Stack>

                                                    <Divider/>
                                                </>
                                            )}

                                            <Stack direction={{xs: "column", xl: "row"}} spacing={2} alignItems="flex-start">
                                                <Box sx={{flex: 1, minWidth: 260}}>
                                                    <Typography variant="subtitle2" className="mb-2">{t("repairs.partsUsed")}</Typography>
                                                    <Forms.Select
                                                        disabled={repairClosed || task.status !== "in_progress"}
                                                        placeholder={t("repairs.addPart")}
                                                        value={null}
                                                        options={(devicePartsMap?.[deviceId] || []).map((part) => ({
                                                            value: part._id,
                                                            label: `${part.name} (skladem ${part.stockQuantity} ks)`
                                                        }))}
                                                        onChange={(option) => addPartToItem(deviceRepair._id, task._id, option.value)}
                                                    />
                                                    <Stack spacing={1} className="mt-2">
                                                        {(task.partsUsed || []).length === 0 && (
                                                            <Typography variant="body2" color="text.secondary">{t("repairs.noParts")}</Typography>
                                                        )}
                                                        {(task.partsUsed || []).map((item, index) => (
                                                            <Box key={item._id || index} className="border rounded px-2 py-1 bg-white">
                                                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                                                    <Box>
                                                                        <Typography variant="body2">
                                                                            {item.part?.name
                                                                                || partByIdMap[item.part?._id || item.part]?.name
                                                                                || item.part?._id
                                                                                || item.part}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {item.createdAt ? new Date(item.createdAt).toLocaleString("cs-CZ") : ""}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Tooltip title="Odebrat díl a vrátit na sklad">
                                                                        <span>
                                                                            <IconButton
                                                                                size="small"
                                                                                color="error"
                                                                                disabled={repairClosed}
                                                                                onClick={() => removePartFromItem(deviceRepair._id, task._id, item._id)}
                                                                            >
                                                                                <DeleteIcon fontSize="small"/>
                                                                            </IconButton>
                                                                        </span>
                                                                    </Tooltip>
                                                                </Stack>
                                                            </Box>
                                                        ))}
                                                    </Stack>
                                                </Box>

                                                <Box sx={{flex: 1, minWidth: 260}}>
                                                    <Typography variant="subtitle2" className="mb-2">{t("notes")}</Typography>
                                                    <Forms.Text
                                                        disabled={repairClosed || TERMINAL_STATUSES.has(task.status)}
                                                        placeholder={t("addNote")}
                                                        onEnter={(value) => addRepairNote(deviceRepair._id, task._id, value)}
                                                    />
                                                    <Stack spacing={1} className="mt-2">
                                                        {(task.noteList || []).map((note, index) => (
                                                            <Box key={index} className="border rounded px-2 py-1 bg-white">
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {`${note.createdBy?.name || ""} ${note.createdBy?.surname || ""}`.trim()}
                                                                    {" | "}
                                                                    {new Date(note.createdAt).toLocaleString("cs-CZ")}
                                                                </Typography>
                                                                <Typography variant="body2">{note.note}</Typography>
                                                            </Box>
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            </Stack>

                                            <Divider/>

                                            <Stack direction={{xs: "column", xl: "row"}} spacing={2} alignItems="flex-start">
                                                <Box sx={{flex: 1, minWidth: 300}}>
                                                    <Typography variant="subtitle2" className="mb-2">{t("repairs.workSessions")}</Typography>
                                                    <Stack spacing={1}>
                                                        {(task.workSessions || []).length === 0 && (
                                                            <Typography variant="body2" color="text.secondary">
                                                                {t("repairs.noWorkSessions")}
                                                            </Typography>
                                                        )}
                                                        {(task.workSessions || [])
                                                            .slice()
                                                            .sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt))
                                                            .map((session, index) => (
                                                                <Box key={index} className="border rounded px-2 py-1 bg-white">
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {(session.technician?.name || "")} {(session.technician?.surname || "")}
                                                                        {" | "}
                                                                        {new Date(session.startedAt).toLocaleString("cs-CZ")}
                                                                        {" - "}
                                                                        {session.endedAt ? new Date(session.endedAt).toLocaleString("cs-CZ") : "běží"}
                                                                    </Typography>
                                                                    <Typography variant="body2">
                                                                        {(session.durationMinutes ?? 0)} min ({session.source})
                                                                    </Typography>
                                                                    {session.note && <Typography variant="body2">{session.note}</Typography>}
                                                                </Box>
                                                            ))}
                                                    </Stack>
                                                </Box>

                                                <Box sx={{flex: 1, minWidth: 300}}>
                                                    <Typography variant="subtitle2" className="mb-2">{t("repairs.statusHistory")}</Typography>
                                                    <Stack spacing={1}>
                                                        {(task.statusHistory || []).length === 0 && (
                                                            <Typography variant="body2" color="text.secondary">
                                                                {t("repairs.noStatusHistory")}
                                                            </Typography>
                                                        )}
                                                        {(task.statusHistory || [])
                                                            .slice()
                                                            .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))
                                                            .map((entry, index) => (
                                                                <Box key={index} className="border rounded px-2 py-1 bg-white">
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {(entry.changedBy?.name || "")} {(entry.changedBy?.surname || "")}
                                                                        {" | "}
                                                                        {new Date(entry.changedAt).toLocaleString("cs-CZ")}
                                                                    </Typography>
                                                                    <Typography variant="body2">
                                                                        {(entry.from && getStatusLabel(entry.from)) || "-"} {" -> "} {getStatusLabel(entry.to)}
                                                                    </Typography>
                                                                    {entry.note && <Typography variant="body2">{entry.note}</Typography>}
                                                                </Box>
                                                            ))}
                                                    </Stack>
                                                </Box>
                                            </Stack>
                                        </Stack>
                                    </Card>
                                );
                            })}
                        </Stack>
                    </Card>
                );
            })}

            {repairClosed && (
                <Alert severity="info">
                    Oprava je uzavřená. Editace je omezená.
                </Alert>
            )}
            {workLockInfo && (
                <Alert severity="warning">
                    {workLockInfo}
                </Alert>
            )}
            {notifyResult && (
                <Alert severity={notifyResult.failedChannels?.length ? "warning" : "success"}>
                    Informování dokončeno. Úspěšně: {(notifyResult.successfulChannels || []).join(", ") || "-"}.
                    Neúspěšně: {(notifyResult.failedChannels || []).join(", ") || "-"}.
                </Alert>
            )}

            <Box className="flex justify-end">
                <Tooltip title={t("back")}>
                    <Button variant="text" startIcon={<Undo/>} onClick={() => navigate(-1)}>
                        {t("back")}
                    </Button>
                </Tooltip>
            </Box>
        </Box>
    );
};

export default EditRepair;
